import { Component, OnInit, inject } from '@angular/core';
import {Observable, ReplaySubject, Subject, zip} from 'rxjs';
import {Client} from '../../../client/client';
import {ActivatedRoute} from '@angular/router';
import {first, map, mergeMap, tap} from 'rxjs/operators';
import {ClientService} from '../../../client/client.service';
import ClientTip from '../../../client-tip/client-tip';
import {ClientTipService} from '../../../client-tip/client-tip.service';
import {Location} from '@angular/common';
import User from '../../../core/auth/user';
import {AuthService} from '../../../core/auth/auth.service';
import {AlgemeneTip} from '../../../algemene-tip/algemene-tip';
import {AlgemeneTipService} from '../../../algemene-tip/algemene-tip.service';
import {DienstService} from '../../../dienst/dienst.service';
import {Taak} from '../../../dienst/taak';
import {Categorie} from '../../../dienst/categorie';
import {Diensten} from '../../../dienst/diensten';
import {Dienst} from '../../../dienst/dienst';

@Component({
    templateUrl: './tips.page.html',
    styleUrls: ['./tips.page.scss'],
    standalone: false
})
export class TipsPage implements OnInit {
  private authService = inject(AuthService);
  private clientService = inject(ClientService);
  private algemeneTipService = inject(AlgemeneTipService);
  private clientTipService = inject(ClientTipService);
  private dienstenService = inject(DienstService);
  private route = inject(ActivatedRoute);
  private location = inject(Location);

  user$: Observable<User>;
  client$: Observable<Client>;
  algemeneTips$: Subject<Array<AlgemeneTip>> = new ReplaySubject<Array<AlgemeneTip>>(1);
  clientTips$: Subject<Array<ClientTip>> = new ReplaySubject<Array<ClientTip>>(1);
  dienstCategorieTaak$: Observable<{dienst: Dienst, categorie: Categorie, taak: Taak}>;

  ngOnInit(): void {
    this.user$ = this.authService.getUser$().pipe(first());
    this.client$ = this.route.paramMap.pipe(
      map(paramMap => paramMap.get('clientId')),
      mergeMap(id => this.clientService.getClientById$(id))
    );
    this.dienstCategorieTaak$ = zip(
      this.route.paramMap.pipe(map(paramMap => paramMap.get('dienstId'))),
      this.route.paramMap.pipe(map(paramMap => paramMap.get('categorieId'))),
      this.route.paramMap.pipe(map(paramMap => paramMap.get('taakId'))),
      this.dienstenService.getDiensten$()
    ).pipe(map(([dienstId, categorieId, taakId, diensten]: [string, string, string, Diensten]) => diensten.getDienstCategorieTaak(dienstId, categorieId, taakId)));
    zip(this.client$, this.dienstCategorieTaak$).pipe(
      mergeMap(([client, taak]: [Client, {dienst: Dienst, categorie: Categorie, taak: Taak }]) => this.getClientTips$(client, taak.categorie, taak.taak))
    ).subscribe();
    this.dienstCategorieTaak$.pipe(
      mergeMap((taak: {dienst: Dienst, categorie: Categorie, taak: Taak }) => this.getAlgemeneTips$(taak.categorie, taak.taak))
    ).subscribe();
  }

  onAlgemeneTipAdded(dienst: Dienst, categorie: Categorie, taak: Taak, content: string): void {
    this.algemeneTipService
      .postAlgemeneTip$(dienst.getId(), categorie.getId(), taak.getId(), content)
      .pipe(mergeMap(() => this.getAlgemeneTips$(categorie, taak)))
      .subscribe();
  }

  onAlgemeneTipEdited(categorie: Categorie, taak: Taak, [algemeneTip, content]: [AlgemeneTip, string]): void {
    this.algemeneTipService
      .putAlgemeneTip$(algemeneTip.getId(), content)
      .pipe(mergeMap(() => this.getAlgemeneTips$(categorie, taak)))
      .subscribe();
  }

  onAlgemeneTipDeleted(categorie: Categorie, taak: Taak, algemeneTip: AlgemeneTip): void {
    this.algemeneTipService
      .deleteAlgemeneTip$(algemeneTip.getId())
      .pipe(mergeMap(() => this.getAlgemeneTips$(categorie, taak)))
      .subscribe();
  }

  onClientTipAdded(client: Client, dienst: Dienst, categorie: Categorie, taak: Taak, content: string): void {
    this.clientTipService
      .postClientTip$(client.getId(), dienst.getId(), categorie.getId(), taak.getId(), content)
      .pipe(mergeMap(() => this.getClientTips$(client, categorie, taak)))
      .subscribe();
  }

  onClientTipEdited(client: Client, categorie: Categorie, taak: Taak, [clientTip, content]: [ClientTip, string]): void {
    this.clientTipService
      .putClientTip$(clientTip.getId(), content)
      .pipe(mergeMap(() => this.getClientTips$(client, categorie, taak)))
      .subscribe();
  }

  onClientTipDeleted(client: Client, categorie: Categorie, taak: Taak, clientTip: ClientTip): void {
    this.clientTipService
      .deleteClientTip$(clientTip.getId())
      .pipe(mergeMap(() => this.getClientTips$(client, categorie, taak)))
      .subscribe();
  }

  navigateBack(): void {
    this.location.back();
  }

  private getAlgemeneTips$(categorie: Categorie, taak: Taak): Observable<Array<AlgemeneTip>> {
    return this.algemeneTipService
      .getAlgemeneTips$(categorie.getId(), taak.getId())
      .pipe(tap(algemeneTips => this.algemeneTips$.next(algemeneTips)));
  }

  private getClientTips$(client: Client, categorie: Categorie, taak: Taak): Observable<Array<ClientTip>> {
    return this.clientTipService
      .getClientTips$(client.getId(), categorie.getId(), taak.getId())
      .pipe(tap(clientTips => this.clientTips$.next(clientTips)));
  }
}
